/**
 * C-DSM: resolves the effective preset/endpoint mode for a data type that may
 * support presets, endpoints, both, or neither.
 *
 * - Preset-only (`!hasEndpoints`) always resolves to 'preset'.
 * - Endpoint-only (`!hasPreset`) always resolves to 'endpoint'.
 * - Dual-mode (`hasEndpoints && hasPreset`) uses the stored mode when it is a
 *   valid value, otherwise falls back to 'preset' when preset items already
 *   exist in the draft, or 'endpoint' otherwise.
 */

type DataSourceMode = 'preset' | 'endpoint';

type ResolveDataSourceModeArgs = {
  hasEndpoints: boolean;
  hasPreset: boolean;
  storedMode: unknown;
  hasPresetItems: boolean;
};

function isDataSourceMode(value: unknown): value is DataSourceMode {
  return value === 'preset' || value === 'endpoint';
}

export function resolveDataSourceMode({
  hasEndpoints,
  hasPreset,
  storedMode,
  hasPresetItems,
}: ResolveDataSourceModeArgs): DataSourceMode {
  if (!hasEndpoints) {
    return 'preset';
  }

  if (!hasPreset) {
    return 'endpoint';
  }

  if (isDataSourceMode(storedMode)) {
    return storedMode;
  }

  return hasPresetItems ? 'preset' : 'endpoint';
}
